import { from, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, take } from 'rxjs/operators';
import { Strings } from '../generated/strings';
import { RpcForwardAutoClient } from './forward/rpc-forward-auto-client';
import {
    RpcForwardDownKey,
    RpcForwardExecuteReportData,
    RpcForwardNotifyReportData,
    RpcForwardReportData,
    RpcForwardResponse,
    RpcForwardType
} from './forward/rpc-forward-model';
import { RpcForwardUpSubjectServer } from './forward/rpc-forward-up-subject-server';
import { Rpc } from './rpc';
import { RpcRelationshipType } from './rpc-base';

/**
 * RPC forwarder class.
 * @dynamic
 */
export class RpcForwarder {
    private static rpcForwardUpSubjectServer: RpcForwardUpSubjectServer<any>;
    private static ready: Observable<void>;
    private static forwardMap: Map<string, RpcServiceForwarder> = new Map<string, RpcServiceForwarder>();
    public static waitForReady(rpc: Rpc) {
        // if we are ready to forward, just return an observable.
        if (rpc.stateActive && RpcForwarder.ready) {
            return of(null);
        }

        // if we are already waiting for the rpc, then return that observable
        if (RpcForwarder.ready) {
            return RpcForwarder.ready;
        }

        // if we are not ready to forward, then set up forwarding based on our window type and current rpc state
        if (MsftSme.isShell()) {
            // If we are shell then setup the the rpc subject server
            RpcForwarder.rpcForwardUpSubjectServer = new RpcForwardUpSubjectServer(rpc);
            RpcForwarder.rpcForwardUpSubjectServer.subject.subscribe(data => {
                RpcForwarder.onForwardReceived(data.data).then(data.deferred.resolve, data.deferred.reject);
            });
        } else {
            if (!rpc.stateActive) {
                // return the first rpc state change that sets the state to active.
                // then register our rpc command before returning
                RpcForwarder.ready = rpc.stateChanged
                    .pipe(
                        filter(active => active),
                        take(1),
                        map(() => rpc.register(RpcForwardDownKey.command, RpcForwarder.onForwardReceived)));
                return RpcForwarder.ready;
            } else {

                // If we are not shell, then setup rpc registration for forwarded messages
                rpc.register(RpcForwardDownKey.command, RpcForwarder.onForwardReceived);
            }
        }

        RpcForwarder.ready = of(null);
        return RpcForwarder.ready;
    }

    public static register(serviceId: string, forwarder: RpcServiceForwarder): void {
        // throw an error if the service has already been registered
        if (RpcForwarder.forwardMap.has(serviceId)) {
            const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.ForwarderIdConflict.message;
            throw new Error(message.format(serviceId));
        }

        // register the forwarder using the serviceId
        RpcForwarder.forwardMap.set(serviceId, forwarder);
    }

    private static onForwardReceived(data: RpcForwardReportData): Promise<RpcForwardResponse<any>> {
        if (!RpcForwarder.forwardMap.has(data.service)) {
            const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.ForwarderIdNotFound.message;
            return Promise.reject(message.format(data.service));
        } else {
            const target = RpcForwarder.forwardMap.get(data.service);
            return target.handleForwardedMessage(data)
                .pipe(
                    map(result => <RpcForwardResponse<any>>{ result: result }),
                    catchError(error => {
                        // on error, make sure the error is serializable and return it as an observable
                        return of({ error: RpcForwarder.ensureSerializable(error) });
                    }))
                .toPromise();
        }
    }

    /**
     * Transform object into something that can be serializable by dropping
     * any non-serializable properties
     *
     * @param obj the object to make serializable.
     * @return a new object that is serializable
     */
    public static ensureSerializable(obj: any): any {
        const type = typeof obj;
        if (type === 'string' || type === 'number' || type === 'boolean') {
            return obj;
        }
        if (obj && type === 'object') {
            if (Array.isArray(obj)) {
                return (<Array<any>>obj).map(v => RpcForwarder.ensureSerializable(v));
            }

            const keys = Object.keys(obj);
            const result = {};
            keys.forEach(key => {
                result[key] = RpcForwarder.ensureSerializable(obj[key]);
            });
            return result;
        }

        // We will return null for symbol, function, null, undefined
        return null;
    }
}

/**
 * Base class to allow 2+ instances of a service to behave as one across the iframe boundary
 * three mechanisms are surfaced for communication:
 * - Execute: expects a response with data from the receiver
 * - Notify: expects a response with no data from the receiver just for confirmation that it was received
 * - Init: always called from a child instance, used to synchronize parent and child as the child starts up
 */
export abstract class RpcServiceForwarder {

    /**
     * Instantiates a new instance of the RpcServiceForwarder
     */
    constructor(private serviceId: string, protected rpc: Rpc) {
        RpcForwarder.register(this.serviceId, this);
    }

    /**
     * Called when a forwarded message is received from the rpc.
     * @param data The RpcForwardReportData of the request
     * @returns an observable for the result of the request call
     */
    public handleForwardedMessage(data: RpcForwardReportData): Observable<any> {
        // in the future Rpc should have no knowledge of shell an module. Only child/parent.
        const fromRpc = this.rpc.isShell ? RpcRelationshipType.Child : RpcRelationshipType.Parent;
        switch (data.type) {
            case RpcForwardType.Init:
                return this.onForwardInit();
            case RpcForwardType.Execute:
                return this.onForwardExecute(fromRpc, data.name, (<RpcForwardExecuteReportData>data).arguments);
            case RpcForwardType.Notify:
                return this.onForwardNotify(fromRpc, data.name, (<RpcForwardNotifyReportData>data).value);
            default: {
                const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.ForwarderUnknownType.message;
                return throwError(() => message.format(data.type));
            }
        }
    }

    /**
     * Initializes the service. The serviceReady observable is assigned to the output of this function.
     */
    public initialize(): Observable<boolean> {
        return RpcForwarder.waitForReady(this.rpc)
            .pipe(
                mergeMap(() => {
                    if (!this.canForward(RpcRelationshipType.Parent)) {
                        // since we dont have a parent we can stop here
                        return of(null);
                    }

                    // other wise we have a parent and should forward an init message
                    const data: RpcForwardReportData = {
                        service: this.serviceId,
                        type: RpcForwardType.Init
                    };

                    return from(RpcForwardAutoClient.forward<any>(this.rpc, data));
                }),
                map(result => result ? this.onForwardInitResponse(result) : null),
                map(() => true),
                take(1));
    }

    /**
     * Reinitialize the forwarded service. This should poll any important data from parents.
     */
    public reinitializeForwardedData(): Observable<void> {
        if (!this.canForward(RpcRelationshipType.Parent)) {
            // since we dont have a parent we can stop here
            return of(null);
        }

        // other wise we have a parent and should forward an init message
        const data: RpcForwardReportData = {
            service: this.serviceId,
            type: RpcForwardType.Init
        };

        return from(RpcForwardAutoClient.forward<any>(this.rpc, data))
            .pipe(map(result => result ? this.onForwardInitResponse(result) : null));
    }

    /**
     * Called on a child service instance when onForwardInit returns from the parent
     * @param data The response from the forwardInit call
     */
    protected abstract onForwardInitResponse(data: RpcForwardResponse<any>): void;

    /**
     * Called when a new instance of the service in another window is initialized and needs to synchronize with its parent
     * @param from The RpcRelationshipType that this request is from
     * @returns an observable for the all the values needed to initialize the service
     */
    protected abstract onForwardInit(): Observable<any>;

    /**
     * Called when the forwarded services counterpart wants to get data from the parent
     * @param from The RpcRelationshipType that this request is from
     * @param name The name of the method to forward to
     * @param args The arguments of the method
     * @returns an observable for the result of the method call
     */
    protected abstract onForwardExecute(from: RpcRelationshipType, name: string, args: any[]): Observable<any>;

    /**
     * Called when the forwarded services counterpart sends a notify message
     * @param from The RpcRelationshipType that this request is from
     * @param name The name of the property to change
     * @param value The new value of the property
     * @returns an observable that completes when the property has been changed.
     */
    protected abstract onForwardNotify(from: RpcRelationshipType, name: string, value: any): Observable<void>;

    /**
     * Creates an observable that errors with name not found
     * @returns an observable that will error with a name not found message
     */
    protected nameNotFound(name: string): Observable<any> {
        return throwError(() => new Error(`${name} not found in forwarded service: ${this.serviceId}`));
    }

    /**
     * Determines if a message can be forwarded to the specified RpcRelationshipType
     * @param to The RpcRelationshipType that needs to be checked
     * @returns true if messages can be forwarded to the specified RpcRelationshipType
     */
    protected canForward(to: RpcRelationshipType) {
        // we can only forward if the rpc is active
        if (this.rpc.stateActive) {
            if (to === RpcRelationshipType.Parent) {
                // we can only forward to parents if we are an extension window
                return MsftSme.isExtension();
            } else if (to === RpcRelationshipType.Child) {
                // when trying to forward to a child, we have to make sure that the rpc outbound is actually ready.
                // rpc.StateActive will report true always fro the shell
                if (!this.rpc.rpcManager || !this.rpc.rpcManager.rpcOutbound) {
                    return false;
                }

                // we can only forward to parents if we have child windows
                return MsftSme.isShell() && window.frames.length > 0;
            }
        }
        return false;
    }

    /**
     * Forwards a execution of some named method to the target relationship type
     * @param to The RpcRelationshipType that this request is intended for
     * @param name The name of the method to execute
     * @param value The arguments for the method
     * @returns an observable for the result of the method call
     */
    protected forwardExecute<T>(to: RpcRelationshipType, name: string, args: any[]): Observable<T> | void {
        // if we cant forward then just return
        if (!this.canForward(to)) {
            return;
        }

        const data: RpcForwardExecuteReportData = {
            arguments: args,
            name: name,
            service: this.serviceId,
            type: RpcForwardType.Execute
        };

        return from(RpcForwardAutoClient.forward<any>(this.rpc, data))
            .pipe(map(response => {
                if (response.error) {
                    throw response.error;
                } else {
                    return response.result;
                }
            }));
    }

    /**
     * Forwards a notification of some state change to the target relationship type
     * @param to The RpcRelationshipType that this request is intended for
     * @param name The name of the state change
     * @param value The new value of some state
     * @returns an observable that completes when the state has been changed on the target instance.
     */
    protected forwardNotify(to: RpcRelationshipType, name: string, value: any, noPrimaryFrame = false): Observable<void> | void {
        // if we cant forward then just return
        if (!noPrimaryFrame && !this.canForward(to)) {
            return;
        }

        const data: RpcForwardNotifyReportData = {
            value: value,
            name: name,
            service: this.serviceId,
            type: RpcForwardType.Notify
        };

        return from(RpcForwardAutoClient.forward<any>(this.rpc, data))
            .pipe(map(response => {
                if (response.error) {
                    throw response.error;
                } else {
                    return response.result;
                }
            }));
    }
}
